import { XMarkIcon } from "@heroicons/react/24/outline"
import classNames from "classnames"
import { ALERT_ICON_MAP, resolveAlertClasses } from "components/Alert/Alert"
import { StatusVariant } from "components/DesignSystemUtils/StatusVariant"
import { useTranslate } from "hooks/useTranslate"
import { PropsWithChildren } from "react"
import { ToastContainer, ToastContainerProps, TypeOptions } from "react-toastify"

import "react-toastify/dist/ReactToastify.css"

/**
 * Theme name without built-in react-toastify styles, so the Alert classes
 * set the toast colors.
 */
const TOAST_THEME = "status"

function toStatusVariant(type: TypeOptions | undefined): StatusVariant {
  return !type || type === "default" ? "neutral" : type
}

export interface NotificationProps extends PropsWithChildren<ToastContainerProps> {
  message?: string
}

/**
 * Displays notification to grab attention. The notification typically
 * disappears after a few seconds. Internally this uses `react-toastify`:
 * @link https://github.com/fkhadra/react-toastify
 *
 * @see 'src/hooks/toast.ts' where notification toasts are created.
 * These end up being shown within this component. Therefore this component should
 * always be added top-level (e.g., `App.tsx`).
 */
export const Notification = ({ ...props }: NotificationProps) => {
  const t = useTranslate()

  return (
    <ToastContainer
      data-testid="Alert"
      closeButton={(close) => (
        <button type="button" onClick={close.closeToast} className="self-start">
          <span className="sr-only">{t("button.close")}</span>
          <XMarkIcon className="ml-1 size-5" />
        </button>
      )}
      icon={({ type }) => {
        const variant = toStatusVariant(type)
        const Icon = ALERT_ICON_MAP[variant]
        return <Icon className={classNames("size-6 shrink-0", resolveAlertClasses(variant).iconClassName)} />
      }}
      theme={TOAST_THEME}
      {...props}
      toastClassName={(context) =>
        classNames(
          // `!items-start` keeps the icon next to the first line of a message that wraps,
          // where react-toastify centers it vertically
          "relative my-2 flex h-full cursor-pointer justify-center overflow-hidden !items-start !rounded-md !p-4 !shadow-md text-body1/regular",
          resolveAlertClasses(toStatusVariant(context?.type)).containerClassName
        )
      }
      className="!m-2 flex !max-w-[92%] items-start"
    />
  )
}
